import { useEffect, useRef, useState } from 'react';

// Pages that show a stored report (Social Presence, Performance & Content
// Intelligence, Market, Predictive, Audience, Performance Intelligence Engine)
// get it at once; when it's old — or the user presses Refresh — the server
// fetches the latest numbers in the background and answers `refreshing: true`.
// The page says so at the top, quietly re-asks every 15 s and swaps the new
// data in.

const POLL_MS = 15000;
const MAX_POLLS = 8; // ~2 minutes

export type FreshnessState = 'idle' | 'refreshing' | 'updated' | 'gave_up';

export function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (Number.isNaN(minutes)) return '';
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

/**
 * While the server says `refreshing` (or `preparing` a first report), call
 * `poll` (a quiet re-fetch that updates the page's data) every `intervalMs`
 * (15 s; 5 s while preparing), up to `maxPolls` times.
 */
export function useBackgroundRefresh(
  refreshing: boolean | undefined,
  poll: () => Promise<unknown>,
  { intervalMs = POLL_MS, maxPolls = MAX_POLLS }: { intervalMs?: number; maxPolls?: number } = {},
): FreshnessState {
  const [state, setState] = useState<FreshnessState>('idle');
  const [tick, setTick] = useState(0);
  const polls = useRef(0);
  const pollRef = useRef(poll);
  pollRef.current = poll;

  useEffect(() => {
    if (!refreshing) {
      if (polls.current === 0) {
        setState('idle');
        return;
      }
      // A poll brought the new data in.
      polls.current = 0;
      setState('updated');
      const t = setTimeout(() => setState('idle'), 5000);
      return () => clearTimeout(t);
    }
    if (polls.current >= maxPolls) {
      setState('gave_up');
      return;
    }
    setState('refreshing');
    const t = setTimeout(() => {
      polls.current += 1;
      pollRef.current()
        .catch(() => undefined)
        .finally(() => setTick((n) => n + 1));
    }, intervalMs);
    return () => clearTimeout(t);
  }, [refreshing, tick, intervalMs, maxPolls]);

  return state;
}

/**
 * Top-of-page freshness line: "Last updated … · Refresh", or a banner while
 * newer data is being fetched. `onRefresh` asks the server for newer data
 * (the endpoint with `refresh=true`) and puts its answer in the page; the
 * server refuses a refresh of data made in the last 10 minutes, which shows
 * as "Already up to date".
 */
export function DataFreshnessBanner({
  updatedAt,
  state,
  onRefresh,
}: {
  updatedAt?: string | null;
  state: FreshnessState;
  onRefresh?: () => Promise<unknown>;
}) {
  const [asking, setAsking] = useState(false);
  const [upToDate, setUpToDate] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const ago = timeAgo(updatedAt);

  useEffect(() => {
    if (!upToDate) return;
    const t = setTimeout(() => setUpToDate(false), 4000);
    return () => clearTimeout(t);
  }, [upToDate]);

  const refresh = async () => {
    if (!onRefresh || asking) return;
    setAsking(true);
    setUpToDate(false);
    try {
      await onRefresh();
    } catch {
      // the page keeps what it shows; the next visit tries again
    } finally {
      setAsking(false);
      // Still idle once the answer is in: nothing newer was started.
      setTimeout(() => setUpToDate(stateRef.current === 'idle'), 0);
    }
  };

  if (state === 'refreshing') {
    return (
      <div role="status" className="flex items-start gap-3 px-4 py-3 bg-sky-50 border border-sky-200 rounded-xl text-sm text-sky-900">
        <i className="ri-loader-4-line animate-spin text-lg text-sky-600 mt-px" />
        <div>
          <p className="font-semibold">Refreshing with your latest data in the background</p>
          <p className="text-sky-800/80 text-xs mt-0.5">
            {ago ? `You're seeing numbers from ${ago}. ` : ''}This page updates by itself when the new numbers are ready — no need to reload.
          </p>
        </div>
      </div>
    );
  }
  if (state === 'updated') {
    return (
      <div role="status" className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800">
        <i className="ri-checkbox-circle-line text-lg text-emerald-600" />
        <p className="font-semibold">Updated with your latest data.</p>
      </div>
    );
  }
  if (state === 'gave_up') {
    return (
      <div role="status" className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
        <i className="ri-error-warning-line text-lg text-amber-600 mt-px" />
        <div>
          <p className="font-semibold">Couldn't fetch your latest numbers right now</p>
          <p className="text-amber-800/80 text-xs mt-0.5">
            {ago ? `Showing data from ${ago}. ` : ''}We'll try again the next time you open this page.
          </p>
        </div>
      </div>
    );
  }
  if (!ago) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500">
      <i className="ri-time-line" />
      <span>Last updated {ago}</span>
      {onRefresh && (
        <>
          <span aria-hidden>·</span>
          {asking ? (
            <span className="inline-flex items-center gap-1 text-slate-500">
              <i className="ri-loader-4-line animate-spin" /> Checking for newer data…
            </span>
          ) : upToDate ? (
            <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
              <i className="ri-check-line" /> Already up to date
            </span>
          ) : (
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center gap-1 font-semibold text-blue-600 hover:text-blue-700 hover:underline"
            >
              <i className="ri-refresh-line" /> Refresh
            </button>
          )}
        </>
      )}
    </div>
  );
}

// While preparing a first report: ask every 5 s for up to ~3 minutes.
export const PREPARING_POLL = { intervalMs: 5000, maxPolls: 36 };

/** In place of the page while its first report is built (nothing stored yet). */
export function PreparingNotice({ state, onRetry }: { state: FreshnessState; onRetry?: () => void }) {
  if (state === 'gave_up') {
    return (
      <div role="status" className="flex flex-col items-center justify-center min-h-[320px] bg-white border border-amber-200 rounded-2xl p-8 text-center">
        <i className="ri-time-line text-4xl text-amber-500 mb-3" />
        <p className="text-base font-semibold text-gray-900">Your first report is taking longer than usual</p>
        <p className="text-sm text-gray-500 mt-1 max-w-md">It's still being prepared in the background. Check back in a minute.</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg text-xs font-semibold"
          >
            Check again
          </button>
        )}
      </div>
    );
  }
  return (
    <div role="status" className="flex flex-col items-center justify-center min-h-[320px] bg-white border border-slate-200 rounded-2xl p-8 text-center">
      <i className="ri-loader-4-line animate-spin text-4xl text-blue-500 mb-3" />
      <p className="text-base font-semibold text-gray-900">Preparing your first report…</p>
      <p className="text-sm text-gray-500 mt-1 max-w-md">
        We're reading your connected accounts. This usually takes under 30 seconds — the page fills in by itself.
      </p>
    </div>
  );
}
